<template>
  <el-dialog
    :model-value="modelValue"
    title="从网盘选择素材"
    width="720px"
    :close-on-click-modal="false"
    @update:model-value="v => emit('update:modelValue', v)"
    @open="onOpen"
  >
    <div v-loading="loading" class="picker-body">
      <!-- 定位失败 -->
      <el-alert
        v-if="error && !loading"
        type="warning"
        :title="error"
        :closable="false"
      />

      <!-- 空态 -->
      <el-empty
        v-else-if="!loading && !error && items.length === 0"
        description="该项目的「0 素材」文件夹还是空的，请先在 AIGC 网盘中上传参考素材"
      />

      <!-- 文件网格 -->
      <div v-else class="file-grid">
        <div
          v-for="item in items"
          :key="item.union_id"
          class="file-card"
          :class="{ 'is-selected': selectedId === item.union_id, 'is-disabled': !isTextFile(item) }"
          @click="toggleSelect(item)"
        >
          <div class="thumb">
            <img
              v-if="item.file_type === 'image'"
              :src="item.cdn_url"
              fit="cover"
              loading="lazy"
            />
            <div v-else class="thumb-icon">
              <el-icon size="32">
                <VideoPlay v-if="item.file_type === 'video'" />
                <Headset v-else-if="item.file_type === 'audio'" />
                <Document v-else />
              </el-icon>
            </div>
            <span class="type-badge">{{ typeLabel(item.file_type) }}</span>
          </div>
          <div class="name" :title="item.display_name">{{ item.display_name || item.filename }}</div>
        </div>
      </div>
    </div>

    <template #footer>
      <el-button @click="emit('update:modelValue', false)">取消</el-button>
      <el-button
        type="primary"
        :disabled="!selectedItem || !isTextFile(selectedItem)"
        :loading="importing"
        @click="confirmSelect"
      >
        导入选中文件
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { getNetdiskAssetsFolder, importNetdiskText, ingestNovelUrl } from '../../api/project'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  projectId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'select', 'select-novel'])

const loading = ref(false)
const importing = ref(false)
const error = ref('')
const items = ref([])
const selectedId = ref(null)

// 由 amj 的 restUrl 推导 aigc-node 的 REST 基址（兼容子目录安装）
const aigcBase = (() => {
  const restUrl = window.amjVueSettings?.restUrl || '/wp-json/amj/v1/'
  return restUrl.replace(/\/amj\/v1\/?$/i, '/aigc/v1')
})()

const selectedItem = computed(() =>
  items.value.find(i => i.union_id === selectedId.value) || null
)

const typeLabel = (t) =>
  ({ image: '图片', video: '视频', audio: '音频', other: '文件' })[t] || '文件'

// 仅文本类内容可回填为小说原文
const isTextFile = (item) => {
  const name = String(item.display_name || item.filename || '').toLowerCase()
  return /\.(txt|md|text)$/.test(name)
}

const toggleSelect = (item) => {
  if (!isTextFile(item)) {
    ElMessage.info('该文件不是文本格式，无法作为小说原文导入')
    return
  }
  selectedId.value = item.union_id
}

const onOpen = async () => {
  loading.value = true
  error.value = ''
  items.value = []
  selectedId.value = null

  try {
    // 1. 服务端幂等定位（必要时创建）0 素材目录
    const res = await getNetdiskAssetsFolder(props.projectId)
    const folderId = res?.data?.folder_id

    // 2. 直读 aigc-node 网盘 REST（同站登录态，复用 wp_rest nonce）
    const resp = await fetch(`${aigcBase}/results?folder_id=${folderId}&per_page=100`, {
      headers: {
        'X-WP-Nonce': window.amjVueSettings?.nonce || ''
      }
    })
    if (!resp.ok) {
      throw new Error(`网盘接口请求失败（${resp.status}），请确认网盘插件已激活且账号有权限`)
    }
    const json = await resp.json()
    items.value = Array.isArray(json?.data?.items) ? json.data.items : []
  } catch (e) {
    console.error('[NetDiskPicker] load failed:', e)
    error.value = e.message || '加载网盘文件失败'
  } finally {
    loading.value = false
  }
}

const confirmSelect = async () => {
  const item = selectedItem.value
  if (!item) return

  importing.value = true
  try {
    const name = item.display_name || item.filename

    // 超大文本（>2MB）走分段解析：服务端下载落盘 + 建索引，随后打开阅读器
    if (Number(item.size_bytes) > 2 * 1024 * 1024) {
      await ingestNovelUrl(props.projectId, item.cdn_url, name)
      ElMessage.success('长文已解析，请在阅读器中选择要导入的章节')
      emit('select-novel', { name })
      emit('update:modelValue', false)
      return
    }

    // 小文件：通过后端代理读取文本内容（浏览器直连 CDN 会被 CORS 拦截）
    const res = await importNetdiskText(item.cdn_url)
    const text = res?.data?.content ?? ''
    if (!text) {
      throw new Error('文件内容为空')
    }
    emit('select', { name, text })
    emit('update:modelValue', false)
  } catch (e) {
    console.error('[NetDiskPicker] import failed:', e)
    ElMessage.error(e.message || '导入失败，请重试')
  } finally {
    importing.value = false
  }
}
</script>

<style scoped lang="scss">
.picker-body {
  min-height: 320px;
}

.file-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  max-height: 420px;
  overflow-y: auto;
}

.file-card {
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    background: #f5f7fa;
  }

  &.is-selected {
    border-color: #409eff;
    background: #ecf5ff;
  }

  &.is-disabled .thumb {
    opacity: 0.6;
  }

  .thumb {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 6px;
    overflow: hidden;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .type-badge {
      position: absolute;
      right: 4px;
      top: 4px;
      font-size: 11px;
      line-height: 16px;
      padding: 0 5px;
      border-radius: 3px;
      color: #fff;
      background: rgba(0, 0, 0, 0.55);
    }
  }

  .name {
    margin-top: 6px;
    font-size: 12px;
    color: #303133;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }
}
</style>
